import React, { useCallback, useEffect, useState } from "react";
import { toast } from "./ui/sonner";
import { RfqCompareTable } from "./RfqCompareTable";
import { CalendarClock, ChevronDown, ChevronRight, FileText, Scale } from "lucide-react";
import { fmtD } from "../lib/date";
import { openAuthedPdf } from "../lib/pdf";

import { T } from "../lib/i18n";

const STATUS_CLS = { draft: "bg-amber-100 text-amber-900", approved: "bg-emerald-50 text-emerald-700", open: "bg-blue-50 text-blue-700", negotiating: "bg-amber-50 text-amber-800", review: "bg-violet-50 text-violet-700", contracted: "bg-emerald-600 text-white", rejected: "bg-red-50 text-red-700" };

/* 项目指挥中心：分包报价对比（全部工种）+ 踏勘日程 */
export const RfqComparePanel = ({ api, id, zh, refreshTick }) => {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState([]);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => {
    api.get(`/projects/${id}/rfq/compare`).then(({ data: d }) => setData(d)).catch(() => setData({ groups: [] }));
    api.get(`/rfq/site-visits`, { params: { projectId: id } }).then(({ data: d }) => setVisits(d.items || [])).catch(() => setVisits([]));
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshTick]);
  const select = async (g, r) => {
    const amt = window.prompt(zh ? "确认成交金额 $" : "Agreed amount $", String(r.quote || ""));
    if (amt == null) return;
    const start = window.prompt(zh ? "确认开工日期 (YYYY-MM-DD，可空)" : "Start date", r.startDate || "") || "";
    try { await api.post(`/rfq/${g.rfqId}/select`, { inviteId: r.inviteId, amount: Number(amt), startDate: start }); toast.success(zh ? "已选定并提交工程经理预算审核" : "Submitted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || T("失败")); }
  };
  if (!data || (data.groups.length === 0 && visits.length === 0)) return null;
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = visits.filter((v) => v.date >= today);
  return (
    <div className="bg-white rounded-2xl border border-teal-100 shadow-sm" data-testid="hub-rfq-compare" data-groups={data.groups.length}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="hub-rfq-compare-toggle">
        {open ? <ChevronDown className="h-4 w-4 text-teal-700" /> : <ChevronRight className="h-4 w-4 text-teal-700" />}
        <Scale className="h-4 w-4 text-teal-700" />
        <span className="font-bold text-slate-800 text-sm">{zh ? "分包报价对比 · 踏勘日程" : "Sub quotes comparison · site visits"}</span>
        <span className="text-[11px] text-slate-400">{zh ? `${data.groups.length} 个工种 · ${upcoming.length} 场待踏勘` : `${data.groups.length} trades · ${upcoming.length} visits`}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {upcoming.length > 0 && (
            <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-2.5" data-testid="hub-rfq-visits">
              <p className="text-[11px] font-semibold text-teal-900 inline-flex items-center gap-1 mb-1"><CalendarClock className="h-3.5 w-3.5" />{zh ? "分包现场踏勘（已入项目日历 · 当天 07:30 铃铛 + 短信提醒 PM）" : "Site visits (in project calendar · 07:30 reminder)"}</p>
              <ul className="flex flex-wrap gap-1.5">
                {upcoming.map((v) => (
                  <li key={v.id} data-testid={`hub-visit-${v.inviteId}`} className={`text-[11px] px-2 py-1 rounded-lg border bg-white ${v.date === today ? "border-brand-300 text-brand-600 font-semibold" : "border-teal-200 text-slate-700"}`}>
                    {fmtD(v.date)}{v.time ? ` ${v.time}` : ""} · {v.companyName} · {v.tradeName}{v.date === today ? (zh ? " · 今天" : " · today") : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.groups.map((g) => (
            <div key={g.rfqId} data-testid={`hub-rfq-group-${g.trade}`} className="space-y-1">
              <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-2">{g.tradeName}<span className={`px-1.5 py-0.5 rounded-full text-[10px] ${STATUS_CLS[g.status] || "bg-slate-100 text-slate-500"}`}>{zh ? g.statusZh : g.status}</span>
                {g.agreed && <span className="text-slate-500">{zh ? "成交" : "Agreed"} ${Number(g.agreed.amount).toLocaleString()}</span>}
                <button data-testid={`hub-rfq-compare-pdf-${g.trade}`} onClick={() => openAuthedPdf(api, `/rfq/${g.rfqId}/compare.pdf`, `rfq-compare-${g.trade}.pdf`).catch((e) => toast.error(e?.response?.data?.detail || e.message))} className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-teal-700" title={zh ? "导出一页对比表 PDF" : "Export PDF"}><FileText className="h-3 w-3" />{zh ? "导出 PDF" : "PDF"}</button></p>
              <RfqCompareTable cmp={g} zh={zh} canSelect={data.canManage && ["open", "negotiating", "rejected"].includes(g.status)} onSelect={(r) => select(g, r)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
